import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'use-debounce'
import type { InboxParticipant } from 'components/inbox/inboxTypes'
import { canaryAdminPath } from 'components/routes/path'
import { ReviewConversationRecipientPills } from 'components/inbox/shared/ReviewConversationRecipientPills'
import { handleAvatarError } from 'components/inbox/utils'
import {
  fetchIssueInboxUsers,
  fetchReviewInboxUsers,
  type InboxUserSearchResult
} from 'components/inbox/services/fetchInboxUsers'

interface CommonProps {
  participants: InboxParticipant[]
  showAddRecipient?: boolean
  contractName?: string
  searchReviewUsers?: (reviewId: number, query?: string) => Promise<InboxUserSearchResult[]>
  searchIssueUsers?: (issueId: number, query?: string) => Promise<InboxUserSearchResult[]>
  onAdditionalRecipientsChange?: (recipients: InboxUserSearchResult[]) => void
}

type Props = CommonProps & (
  | { reviewId: number, issueId?: never }
  | { issueId: number, reviewId?: never }
  | { reviewId?: never, issueId?: never }
)

type RecipientSearchSubject =
  | { kind: 'issue', id: number }
  | { kind: 'review', id: number }

export const PrivateMessageRecipientsField: React.FC<Props> = ({
  participants,
  showAddRecipient = false,
  reviewId,
  issueId,
  contractName,
  searchReviewUsers = fetchReviewInboxUsers,
  searchIssueUsers = fetchIssueInboxUsers,
  onAdditionalRecipientsChange
}) => {
  const [additionalRecipients, setAdditionalRecipients] = useState<InboxUserSearchResult[]>([])
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<InboxUserSearchResult[]>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const searchRequestSequenceRef = useRef(0)
  const [debouncedQuery] = useDebounce(query, 250)

  const nextRequestSequence = (): number => {
    const requestSequence = searchRequestSequenceRef.current + 1
    searchRequestSequenceRef.current = requestSequence
    return requestSequence
  }

  const recipientSearchSubject = useMemo<RecipientSearchSubject | null>(() => {
    if (issueId != null) return { kind: 'issue', id: issueId }
    if (reviewId != null) return { kind: 'review', id: reviewId }

    return null
  }, [issueId, reviewId])

  const searchRecipientUsers = async (
    subject: RecipientSearchSubject,
    nextQuery: string
  ): Promise<InboxUserSearchResult[]> => (
    subject.kind === 'issue'
      ? await searchIssueUsers(subject.id, nextQuery)
      : await searchReviewUsers(subject.id, nextQuery)
  )

  useEffect(() => {
    if (!showAddRecipient) return

    const handleDocumentClick = (event: MouseEvent): void => {
      if (
        containerRef.current != null &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleDocumentClick)
    return () => document.removeEventListener('mousedown', handleDocumentClick)
  }, [showAddRecipient])

  useEffect(() => {
    onAdditionalRecipientsChange?.(additionalRecipients)
  }, [additionalRecipients, onAdditionalRecipientsChange])

  useEffect(() => {
    if (!isDropdownOpen || recipientSearchSubject == null || debouncedQuery.trim() === '') return

    let isMounted = true
    const requestSequence = nextRequestSequence()
    setIsLoading(true)
    setLoadError(null)

    void searchRecipientUsers(recipientSearchSubject, debouncedQuery).then((nextResults) => {
      if (!isMounted || requestSequence !== searchRequestSequenceRef.current) return
      setResults(nextResults)
    }).catch(() => {
      if (!isMounted || requestSequence !== searchRequestSequenceRef.current) return
      setResults([])
      setLoadError('Unable to load contacts. Please try again.')
    }).finally(() => {
      if (!isMounted || requestSequence !== searchRequestSequenceRef.current) return
      setIsLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [debouncedQuery, isDropdownOpen, recipientSearchSubject, searchIssueUsers, searchReviewUsers])

  const availableResults = useMemo(() => (
    results.filter((result) => !additionalRecipients.some(({ id }) => id === result.id))
  ), [additionalRecipients, results])

  const openDropdown = async (): Promise<void> => {
    if (!showAddRecipient || recipientSearchSubject == null) return

    const requestSequence = nextRequestSequence()
    setIsDropdownOpen(true)
    setIsLoading(true)
    setLoadError(null)
    setIsLearnMoreOpen(false)
    window.requestAnimationFrame(() => {
      inputRef.current?.focus()
    })

    try {
      const nextResults = await searchRecipientUsers(recipientSearchSubject, '')
      if (requestSequence !== searchRequestSequenceRef.current) return
      setResults(nextResults)
    } catch {
      if (requestSequence !== searchRequestSequenceRef.current) return
      setResults([])
      setLoadError('Unable to load contacts. Please try again.')
    } finally {
      if (requestSequence === searchRequestSequenceRef.current) {
        setIsLoading(false)
      }
    }
  }

  const handleAddRecipient = (recipient: InboxUserSearchResult): void => {
    setAdditionalRecipients((currentRecipients) => {
      if (currentRecipients.some(({ id }) => id === recipient.id)) return currentRecipients
      return [...currentRecipients, recipient]
    })
    setQuery('')
    setIsDropdownOpen(false)
  }

  const handleRemoveRecipient = (recipientId: number): void => {
    setAdditionalRecipients((currentRecipients) => (
      currentRecipients.filter(({ id }) => id !== recipientId)
    ))
  }

  const toggleLearnMore = (): void => {
    setIsLearnMoreOpen((currentValue) => !currentValue)
  }

  const handleLearnMoreKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      toggleLearnMore()
    }
  }

  const recommendedContactParts = (result: InboxUserSearchResult): {
    organizationName: string
    recommendationCopy: string | null
  } => {
    const organizationName = result.organizationName

    if (result.reviewAuthor) {
      return {
        organizationName,
        recommendationCopy: '(Review writer)'
      }
    }

    if (query.trim() !== '') {
      return {
        organizationName,
        recommendationCopy: null
      }
    }

    if (contractName == null || contractName === '') {
      return {
        organizationName,
        recommendationCopy: '(Recommended contact)'
      }
    }

    return {
      organizationName,
      recommendationCopy: `(Recommended contact for ${contractName} Contract)`
    }
  }

  const renderAdditionalRecipientPill = (recipient: InboxUserSearchResult): React.ReactNode => (
    <div
      key={recipient.id}
      className='message-reviewer-modal__recipient-pill'
    >
      <img
        className='message-reviewer-modal__recipient-avatar'
        src={recipient.avatarUrl}
        alt=''
        onError={(event) => handleAvatarError(event.currentTarget)}
      />
      <span className='message-reviewer-modal__recipient-text'>
        <span className='message-reviewer-modal__recipient-name'>
          {recipient.name},
        </span>
        {recipient.organizationName !== '' && (
          <span className='message-reviewer-modal__recipient-org'>
            {recipient.organizationName}
          </span>
        )}
      </span>
      {showAddRecipient && (
        <button
          type='button'
          className='message-reviewer-modal__recipient-remove'
          aria-label={`Remove ${recipient.name}`}
          onClick={() => handleRemoveRecipient(recipient.id)}
        >
          <i className='fa-solid fa-xmark' aria-hidden='true' />
        </button>
      )}
    </div>
  )

  const renderNoResultsState = (): React.ReactNode => (
    <div className='private-message-recipients-field__no-results' role='status'>
      <div className='private-message-recipients-field__no-results-summary'>
        <span className='private-message-recipients-field__no-results-text'>
          No results match your search. This contact may not yet be registered on Procurated.
          {' '}
          <span
            className='private-message-recipients-field__learn-more'
            role='button'
            tabIndex={0}
            onClick={toggleLearnMore}
            onKeyDown={handleLearnMoreKeyDown}
          >
            (Learn more)
          </span>
        </span>
      </div>

      {isLearnMoreOpen && (
        <ul className='private-message-recipients-field__no-results-details'>
          <li>Confirm your search term contains a valid contact name or email.</li>
          <li>Only registered supplier contacts from the reviewed supplier can be added to the conversation.</li>
          <li>
            Additional supplier contacts can be registered in the
            {' '}
            <a
              className='private-message-recipients-field__details-link'
              href={canaryAdminPath()}
            >
              Canary Admin interface
            </a>
            .
          </li>
          <li>Non-supplier contacts must be registered users who are associated with this contract vehicle. The review must be tagged with a contract vehicle to add non-supplier contacts.</li>
        </ul>
      )}
    </div>
  )

  return (
    <div className='private-message-recipients-field' ref={containerRef}>
      <div className='message-reviewer-modal__recipient-field'>
        <div className='message-reviewer-modal__recipient-label'>To:</div>
        <div className='message-reviewer-modal__recipient-list'>
          <ReviewConversationRecipientPills participants={participants} />
          {additionalRecipients.map(renderAdditionalRecipientPill)}
          {showAddRecipient && !isDropdownOpen && (
            <button
              type='button'
              className='message-reviewer-modal__add-recipient'
              onClick={() => { void openDropdown() }}
            >
              Add recipient
            </button>
          )}
          {showAddRecipient && isDropdownOpen && (
            <input
              ref={inputRef}
              type='text'
              value={query}
              className='message-reviewer-modal__add-recipient message-reviewer-modal__add-recipient--editing'
              onChange={(event) => {
                setQuery(event.target.value)
                setIsLearnMoreOpen(false)
              }}
            />
          )}
        </div>
      </div>

      {showAddRecipient && isDropdownOpen && (
        <div className='private-message-recipients-field__dropdown' data-testid='private-message-recipients-dropdown'>
          {isLoading && (
            <div className='private-message-recipients-field__status'>
              Loading recommended contacts...
            </div>
          )}

          {loadError != null && (
            <div className='private-message-recipients-field__status' role='alert'>
              {loadError}
            </div>
          )}

          {!isLoading &&
            loadError == null &&
            availableResults.length === 0 &&
            (query.trim() !== '' || additionalRecipients.length === 0) && (
            renderNoResultsState()
          )}

          {!isLoading && loadError == null && availableResults.length > 0 && (
            <div className='private-message-recipients-field__results'>
              {availableResults.map((result) => {
                const { organizationName, recommendationCopy } = recommendedContactParts(result)

                return (
                  <button
                    key={result.id}
                    type='button'
                    className='private-message-recipients-field__result'
                    onClick={() => handleAddRecipient(result)}
                  >
                    <img
                      className='private-message-recipients-field__result-avatar'
                      src={result.avatarUrl}
                      alt=''
                      onError={(event) => handleAvatarError(event.currentTarget)}
                    />
                    <span className='private-message-recipients-field__result-text'>
                      <span className='private-message-recipients-field__result-name'>{result.name},</span>
                      {organizationName !== '' && (
                        <span className='private-message-recipients-field__result-org'>
                          {organizationName}
                        </span>
                      )}
                      {recommendationCopy != null && (
                        <span className='private-message-recipients-field__result-recommendation'>
                          {recommendationCopy}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
